import type { ReactNode } from 'react';
import React from 'react';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import type { Props as DynamicLinkProps } from 'Components/frameworks/dynamic-link/DynamicLink';
import 'Components/frameworks/dynamic-link/DynamicLink';
import type { TextProps } from 'Components/frameworks/text/Text';
import 'Components/frameworks/text/Text';
import type { TextSeeMoreProps } from 'Components/frameworks/text-see-more/TextSeeMore';
import 'Components/frameworks/text-see-more/TextSeeMore';
import { CardBody } from './card-body/CardBody';
import { CardHeader } from './card-header/CardHeader';
import type { Media } from './card-media/CardMedia';
import { CardMedia } from './card-media/CardMedia';
import type { CardHeadingProps } from './card-header/card-heading/CardHeading';
import './card-header/card-heading/CardHeading';
import * as layouts from './Card.layouts';
import * as CARD_LAYOUTS from './Card.constants';
export type CardProps = {
  className?: string;
  closeLabel?: string;
  color?: Colors;
  ctaLabel?: string;
  customMedia?: ReactNode;
  dataTestIdPrefix?: string;
  displayLoadMore?: boolean;
  hasFullScreenMode?: boolean;
  heading?: CardHeadingProps;
  id?: string;
  introduction?: TextProps;
  layout?: keyof typeof CARD_LAYOUTS;
  link?: DynamicLinkProps;
  media?: Media;
  seeLessLabel?: string;
  seeMoreLabel?: string;
  text?: TextSeeMoreProps;
  theme?: string;
};
export const Card = ({
  className,
  closeLabel,
  color,
  ctaLabel,
  customMedia,
  dataTestIdPrefix,
  displayLoadMore,
  hasFullScreenMode,
  heading,
  id,
  introduction,
  layout = CARD_LAYOUTS.INLINE_1,
  link,
  media,
  seeLessLabel,
  seeMoreLabel,
  text,
  theme = 'default',
}: CardProps) => {
  const { isTablet } = useResponsive();
  const hasTitle = Boolean(heading?.title);
  const hasCta = Boolean(ctaLabel);
  const header =
    !introduction?.text && !hasTitle ? null : (
      <CardHeader
        color={color}
        dataTestIdPrefix={dataTestIdPrefix}
        heading={heading}
        introduction={introduction}
        link={hasCta ? null : link}
        theme={theme}
      />
    );
  const body =
    !text?.text && !hasCta ? null : (
      <CardBody
        ctaLabel={ctaLabel}
        dataTestIdPrefix={dataTestIdPrefix}
        displayLoadMore={displayLoadMore}
        isTablet={isTablet}
        layout={layout}
        link={link}
        seeLessLabel={seeLessLabel}
        seeMoreLabel={seeMoreLabel}
        text={text}
        theme={theme}
      />
    );
  const picture =
    !media?.url && !customMedia ? null : (
      <CardMedia
        closeLabel={closeLabel}
        hasFullScreenMode={hasFullScreenMode}
        link={hasCta || hasTitle ? null : link}
        media={media}
        theme={theme}
        title={heading?.title}
      >
        {customMedia}
      </CardMedia>
    );
  return body || header || picture ? (
    <article className={className} id={id}>
      {layouts[layout](header, body, picture, dataTestIdPrefix)}
    </article>
  ) : null;
};
